import { Box, Table, Text } from "@mantine/core"
import { useTranslation } from "react-i18next"
import RenderText from "../../../../components/ui/render-text"
import classes from "../analytics.module.css"
import { AnalyticsCard, DrillTable, DrillTitle, EmptyRow } from "../cards/AnalyticsCard"
import { HBarItem, HorizontalBarList } from "../cards/HorizontalBarList"
import { RankBadge, RankColumn, RankTable } from "../cards/RankTable"
import {
  LowMarginOrders,
  MarginByBrand,
  MarginByCustomer,
  MarginByEmployee,
  MarginByProduct,
  MarginEmployeeRow
} from "../types"
import { formatInt, formatMnt, formatMonthDay } from "../utils"

type LoadingProps<T> = { data: T | undefined; isLoading: boolean }

function pctSuffix(pct: number): string {
  return ` · ${pct.toFixed(0)}%`
}

const fmtMnt = formatMnt

// ============ Маржин харилцагчаар ============

export function MarginByCustomerCard({ data, isLoading }: LoadingProps<MarginByCustomer>) {
  const { t } = useTranslation(["dashboard"])
  const rows = data?.data.rows ?? []
  const max = rows.reduce((m, r) => Math.max(m, r.marginAmount), 0)
  const items: HBarItem[] = rows.map(r => ({
    id          : r.partnerId,
    label       : r.partnerName,
    amount      : r.marginAmount,
    percent     : max > 0 ? Math.round((r.marginAmount / max) * 100) : 0,
    valueSuffix : pctSuffix(r.marginPct)
  }))

  return (
    <HorizontalBarList
      title={t("analytics.margin.byCustomer", { defaultValue: "Маржин харилцагчаар" })}
      items={items}
      isLoading={isLoading}
      renderDrill={item => {
        const row = rows.find(r => r.partnerId === item.id)
        if (!row) return null
        return (
          <>
            <DrillTitle>{row.partnerName} · {t("analytics.margin.byProductBreakdown", { defaultValue: "Бараа бүрийн маржин" })}</DrillTitle>
            <DrillTable
              headers={[
                t("analytics.tables.product", { defaultValue: "Бараа" }),
                t("analytics.tables.qty", { defaultValue: "Тоо" }),
                t("analytics.margin.sales", { defaultValue: "Борлуулалт" }),
                t("analytics.margin.amount", { defaultValue: "Маржин" }),
                "%"
              ]}
              rows={row.breakdown.map(b => [
                b.productName,
                `${formatInt(b.qty)} ${t("analytics.tables.pieces", { defaultValue: "ш" })}`,
                fmtMnt(b.sales),
                <Text key="m" component="span" className={classes.up}>{fmtMnt(b.marginAmount)}</Text>,
                <Text key="p" component="span" fw={600}>{b.marginPct.toFixed(0)}%</Text>
              ])}
            />
          </>
        )
      }}
    />
  )
}

// ============ Маржин бараагаар ============

export function MarginByProductCard({ data, isLoading }: LoadingProps<MarginByProduct>) {
  const { t } = useTranslation(["dashboard"])
  const rows = data?.data.rows ?? []
  const max = rows.reduce((m, r) => Math.max(m, r.marginAmount), 0)
  const items: HBarItem[] = rows.map(r => ({
    id          : r.productId,
    label       : r.productName,
    amount      : r.marginAmount,
    percent     : max > 0 ? Math.round((r.marginAmount / max) * 100) : 0,
    valueSuffix : pctSuffix(r.marginPct)
  }))

  return (
    <HorizontalBarList
      title={t("analytics.margin.byProduct", { defaultValue: "Маржин бараагаар" })}
      items={items}
      isLoading={isLoading}
      renderDrill={item => {
        const row = rows.find(r => r.productId === item.id)
        if (!row) return null
        return (
          <>
            <DrillTitle>{row.productName} · {t("analytics.margin.byPartnerBreakdown", { defaultValue: "Харилцагчийн маржин" })}</DrillTitle>
            <DrillTable
              headers={[
                t("analytics.tables.partner", { defaultValue: "Харилцагч" }),
                t("analytics.tables.qty", { defaultValue: "Тоо" }),
                t("analytics.margin.sales", { defaultValue: "Борлуулалт" }),
                t("analytics.margin.amount", { defaultValue: "Маржин" }),
                "%"
              ]}
              rows={row.breakdown.map(b => [
                b.partnerName,
                `${formatInt(b.qty)} ${t("analytics.tables.pieces", { defaultValue: "ш" })}`,
                fmtMnt(b.sales),
                <Text key="m" component="span" className={classes.up}>{fmtMnt(b.marginAmount)}</Text>,
                <Text key="p" component="span" fw={600}>{b.marginPct.toFixed(0)}%</Text>
              ])}
            />
          </>
        )
      }}
    />
  )
}

// ============ Маржин брэндээр ============

export function MarginByBrandCard({ data, isLoading }: LoadingProps<MarginByBrand>) {
  const { t } = useTranslation(["dashboard"])
  const rows = data?.data.rows ?? []
  const max = rows.reduce((m, r) => Math.max(m, r.marginAmount), 0)
  const items: HBarItem[] = rows.map(r => ({
    id          : r.brand,
    label       : r.brand,
    amount      : r.marginAmount,
    percent     : max > 0 ? Math.round((r.marginAmount / max) * 100) : 0,
    valueSuffix : pctSuffix(r.marginPct)
  }))

  return (
    <HorizontalBarList
      title={t("analytics.margin.byBrand", { defaultValue: "Маржин брэндээр" })}
      items={items}
      isLoading={isLoading}
      renderDrill={item => {
        const row = rows.find(r => r.brand === item.id)
        if (!row) return null
        return (
          <>
            <DrillTitle>{row.brand} · {t("analytics.margin.byProductBreakdown", { defaultValue: "Бараа бүрийн маржин" })}</DrillTitle>
            <DrillTable
              headers={[
                t("analytics.tables.product", { defaultValue: "Бараа" }),
                t("analytics.tables.qty", { defaultValue: "Тоо" }),
                t("analytics.margin.sales", { defaultValue: "Борлуулалт" }),
                t("analytics.margin.amount", { defaultValue: "Маржин" }),
                "%"
              ]}
              rows={row.breakdown.map(b => [
                b.productName,
                `${formatInt(b.qty)} ${t("analytics.tables.pieces", { defaultValue: "ш" })}`,
                fmtMnt(b.sales),
                <Text key="m" component="span" className={classes.up}>{fmtMnt(b.marginAmount)}</Text>,
                <Text key="p" component="span" fw={600}>{b.marginPct.toFixed(0)}%</Text>
              ])}
            />
          </>
        )
      }}
    />
  )
}

// ============ Маржин ажилтнаар ============

export function MarginByEmployeeCard({ data, isLoading }: LoadingProps<MarginByEmployee>) {
  const { t } = useTranslation(["dashboard"])
  const rows = data?.data.rows ?? []

  const columns: RankColumn<MarginEmployeeRow>[] = [
    { key: "rank", header: "#", width: "56px", render: (_, idx) => <RankBadge n={idx + 1} /> },
    { key: "name", header: t("analytics.margin.employee", { defaultValue: "Ажилтан" }), render: r => r.name },
    {
      key: "margin",
      header: t("analytics.margin.amount", { defaultValue: "Маржин" }),
      align: "right",
      render: r => <Text component="span" className={classes.amount}>{fmtMnt(r.marginAmount)}</Text>
    },
    { key: "pct", header: "%", align: "right", render: r => `${r.marginPct.toFixed(0)}%` },
    { key: "orders", header: t("analytics.tables.count", { defaultValue: "Захиалга" }), align: "right", render: r => formatInt(r.orderCount) }
  ]

  return (
    <RankTable<MarginEmployeeRow>
      title={t("analytics.margin.byEmployee", { defaultValue: "Маржин ажилтнаар" })}
      rows={rows}
      columns={columns}
      isLoading={isLoading}
      rowKey={r => r.userId}
      renderDrill={r => (
        <>
          <DrillTitle>{r.name} · {t("analytics.margin.byPartnerBreakdown", { defaultValue: "Харилцагчийн борлуулалт" })}</DrillTitle>
          <DrillTable
            headers={[
              t("analytics.tables.partner", { defaultValue: "Харилцагч" }),
              t("analytics.margin.sales", { defaultValue: "Борлуулалт" }),
              t("analytics.margin.amount", { defaultValue: "Маржин" }),
              "%"
            ]}
            rows={r.breakdown.map(b => [
              b.partnerName,
              fmtMnt(b.sales),
              <Text key="m" component="span" className={classes.up}>{fmtMnt(b.marginAmount)}</Text>,
              <Text key="p" component="span" fw={600}>{b.marginPct.toFixed(0)}%</Text>
            ])}
          />
        </>
      )}
    />
  )
}

// ============ Хамгийн бага маржинтай захиалгууд ============

export function LowMarginOrdersCard({ data, isLoading }: LoadingProps<LowMarginOrders>) {
  const { t } = useTranslation(["dashboard"])
  const rows = data?.data.rows ?? []

  return (
    <AnalyticsCard
      title={t("analytics.margin.lowMarginTitle", { defaultValue: "Хамгийн бага маржинтай захиалгууд" })}
      isLoading={isLoading}
    >
      <Box className={classes.tableWrap}>
        <Table className={classes.table} withRowBorders={false}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("analytics.tables.orderNumber", { defaultValue: "Захиалга" })}</Table.Th>
              <Table.Th>{t("analytics.tables.partner", { defaultValue: "Харилцагч" })}</Table.Th>
              <Table.Th data-align="right">{t("analytics.tables.amount", { defaultValue: "Дүн" })}</Table.Th>
              <Table.Th data-align="right">{t("analytics.margin.amount", { defaultValue: "Маржин ₮" })}</Table.Th>
              <Table.Th data-align="right">{t("analytics.margin.pct", { defaultValue: "Маржин %" })}</Table.Th>
              <Table.Th data-align="right">{t("analytics.tables.date", { defaultValue: "Огноо" })}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map(r => {
              // Under 10% is a loss risk; colour is the only signal, as before.
              const tone = r.marginPct < 10 ? "var(--mantine-color-red-8)" : r.marginPct < 15 ? undefined : "var(--mantine-color-green-8)"
              return (
                <Table.Tr key={r.orderId}>
                  <Table.Td><RenderText text={r.orderNumber} fontWeight={600} size="md" /></Table.Td>
                  <Table.Td><RenderText text={r.partnerName} size="md" /></Table.Td>
                  <Table.Td data-align="right"><RenderText isAmount text={fmtMnt(r.orderAmount)} fontWeight={600} size="md" /></Table.Td>
                  <Table.Td data-align="right"><RenderText isAmount text={fmtMnt(r.marginAmount)} fontWeight={600} color={tone} size="md" /></Table.Td>
                  <Table.Td data-align="right"><RenderText isAmount text={`${r.marginPct.toFixed(1)}%`} fontWeight={600} color={tone} size="md" /></Table.Td>
                  <Table.Td data-align="right">{formatMonthDay(r.respondedDate)}</Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      </Box>
      {rows.length === 0 && !isLoading ? <EmptyRow /> : null}
    </AnalyticsCard>
  )
}

